import React, {
    Context,
    createContext,
    FC,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react';
import { useLocation } from 'react-router-dom';

interface MobileMenuState {
    isMobileMenuOpened: boolean;
    toggleMobileMenu: (opened: boolean) => void;
    toggleCorporateStyles: (isCorporate: boolean) => void;
    toggleProfessionStyles: (isProfession: boolean) => void;
    toggleAllianceStyles: (isAlliance: boolean) => void;
    toggleCourseStyles: (isCourse: boolean) => void;
    toggleKidsCoursesStyles: (isKidsCourses: boolean) => void;
    toggleAcademyAllianceStyles: (isAcademyAlliance: boolean) => void;
    isCorporatePage: boolean;
    isProfessionPage: boolean;
    isCoursePage: boolean;
    isAlliancePage: boolean;
    isKidsCoursesPage: boolean;
    isAcademyAlliancePage: boolean;
}

const initState: MobileMenuState = {
    isMobileMenuOpened: false,
    toggleMobileMenu: () => {},
    isCorporatePage: window.location.pathname === '/for-businesses',
    isProfessionPage: window.location.pathname.includes('profession'),
    isCoursePage: window.location.pathname.includes('course'),
    isAlliancePage: window.location.pathname.includes(
        'academy-alliance-request-form'
    ),
    isAcademyAlliancePage: window.location.pathname === '/academy-alliance2',
    isKidsCoursesPage: window.location.pathname.includes('kids-courses'),
    toggleCorporateStyles: () => {},
    toggleAcademyAllianceStyles: () => {},
    toggleProfessionStyles: () => {},
    toggleCourseStyles: () => {},
    toggleAllianceStyles: () => {},
    toggleKidsCoursesStyles: () => {},
};

const MobileMenuContext: Context<MobileMenuState> = createContext(initState);

export const MobileMenuStateProvider: FC<{ children: ReactNode }> = (props) => {
    const [isMobileMenuOpened, setIsMobileMenuOpened] = useState(
        initState.isMobileMenuOpened
    );
    const [isCorporatePage, setIsCorporatePage] = useState(
        initState.isCorporatePage
    );
    const [isAcademyAlliancePage, setIsAcademyAlliancePage] = useState(
        initState.isCorporatePage
    );
    const [isProfessionPage, setIsProfessionPage] = useState(
        initState.isProfessionPage
    );
    const [isAlliancePage, setIsAlliancePage] = useState(
        initState.isAlliancePage
    );
    const [isKidsCoursesPage, setIsKidsCoursesPage] = useState(
        initState.isKidsCoursesPage
    );
    const [isCoursePage, setIsCoursePage] = useState(initState.isCoursePage);
    const location = useLocation();

    const toggleMobileMenu = useCallback((opened: boolean) => {
        setIsMobileMenuOpened(opened);
    }, []);

    const toggleAllianceStyles = useCallback((isAlliance: boolean) => {
        setIsAlliancePage(isAlliance);
    }, []);

    const toggleAcademyAllianceStyles = useCallback(
        (isAcademyAlliance: boolean) => {
            setIsAcademyAlliancePage(isAcademyAlliance);
        },
        []
    );

    const toggleCorporateStyles = useCallback((isCorporate: boolean) => {
        setIsCorporatePage(isCorporate);
    }, []);

    const toggleCourseStyles = useCallback((isCourse: boolean) => {
        setIsCoursePage(isCourse);
    }, []);

    const toggleProfessionStyles = useCallback((isProfession: boolean) => {
        setIsProfessionPage(isProfession);
    }, []);

    const toggleKidsCoursesStyles = useCallback((isKidsCourses: boolean) => {
        setIsKidsCoursesPage(isKidsCourses);
    }, []);

    useEffect(() => {
        isMobileMenuOpened && toggleMobileMenu(false);
    }, [location]);

    useEffect(() => {
        const openMenuClass = 'is-header-menu-open';
        isMobileMenuOpened
            ? document.body.classList.add(openMenuClass)
            : document.body.classList.remove(openMenuClass);
    }, [isMobileMenuOpened]);

    return (
        <MobileMenuContext.Provider
            value={{
                toggleMobileMenu,
                isMobileMenuOpened,
                toggleCorporateStyles,
                isAcademyAlliancePage,
                toggleAcademyAllianceStyles,
                toggleProfessionStyles,
                isCorporatePage,
                isProfessionPage,
                toggleCourseStyles,
                isCoursePage,
                isAlliancePage,
                toggleAllianceStyles,
                toggleKidsCoursesStyles,
                isKidsCoursesPage,
            }}
            {...props}
        />
    );
};

export const useMobileMenuContext = () => useContext(MobileMenuContext);
